import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  useArchiveRadarTracker,
  useRadarCollaborationStatus,
  useRadarOpportunityAction,
  useRadarPursuitProofs,
  useRadarArchives,
  useRadarPursuitRoom,
  useRadarStatus,
  useUpdateRadarPursuit,
} from "@/features/radar/hooks";
import type {
  RadarAlertOpportunity,
  RadarCompanyTracker,
  RadarPursuit,
} from "@/features/radar/api";
import {
  isArchivedOpportunity,
  opportunityMatchesView,
  pursuitMatchesView,
  RADAR_VIEWS,
  trackerMatchesView,
  type RadarView,
} from "@/features/radar/workspace-views";
import { useReadyAuth } from "@/auth/use-auth";

const STAGES: RadarPursuit["stage"][] = [
  "claimed",
  "outreach",
  "meeting",
  "proposal",
  "won",
  "closed_lost",
];
function label(value: string): string {
  return value.replaceAll("_", " ").replace(/\b\w/g, (character) => character.toUpperCase());
}

function shortDate(value: string | null): string {
  if (!value) return "Not set";
  return new Date(value).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function viewFor(raw: string | null): RadarView {
  return RADAR_VIEWS.includes(raw as RadarView) ? (raw as RadarView) : "all";
}

function outlookDraftUrl(pursuit: RadarPursuit): string {
  const company = pursuit.organizations?.legal_name ?? "RADAR lead";
  const members = (pursuit.radar_pursuit_members ?? [])
    .filter((member) => !member.left_at)
    .map((member) => `${member.member_name} (${label(member.member_role)})`)
    .join(", ");
  const notes = (pursuit.radar_pursuit_events ?? [])
    .filter((event) => event.body)
    .slice(0, 6)
    .map((event) => `• ${event.actor_email}: ${event.body}`)
    .join("\n");
  const body = [
    `RADAR lead: ${company}`,
    `Status: ${label(pursuit.stage)}`,
    `Lead: ${pursuit.lead_name || pursuit.lead_email || "To be resolved"}`,
    `Follow-up: ${shortDate(pursuit.follow_up_at)}`,
    members ? `RADAR team: ${members}` : "",
    "",
    "Why this is on our RADAR",
    notes || "Open RADAR to review the governed evidence and relationship context.",
    "",
    "Internal RADAR context — review and remove internal notes before sending externally.",
  ]
    .filter(Boolean)
    .join("\n");
  const params = new URLSearchParams({ subject: `RADAR lead: ${company}`, body });
  return `https://outlook.office.com/mail/deeplink/compose?${params.toString()}`;
}

function AlertOpportunityCard({
  opportunity,
  focused,
}: {
  opportunity: RadarAlertOpportunity;
  focused: boolean;
}): JSX.Element {
  const action = useRadarOpportunityAction();
  const [mode, setMode] = useState<
    "idle" | "claim" | "pass" | "snooze" | "not_relevant" | "field_intel"
  >("idle");
  const [role, setRole] = useState("lead");
  const [reason, setReason] = useState("");
  const [savedMessage, setSavedMessage] = useState("");
  const [followUpDate, setFollowUpDate] = useState(() => {
    const value = new Date();
    value.setDate(value.getDate() + 14);
    return value.toISOString().slice(0, 10);
  });
  const archived = isArchivedOpportunity(opportunity);

  const submit = (
    recipientAction: "claim" | "pass" | "snooze" | "not_relevant" | "field_intel",
    fields: Record<string, unknown> = {}
  ) => {
    action.mutate(
      { alertRecipientId: opportunity.alert_recipient_id, recipientAction, fields },
      {
        onSuccess: () => {
          setMode("idle");
          if (recipientAction === "field_intel") {
            setReason("");
            setSavedMessage("Field knowledge added to this RADAR opportunity.");
          }
        },
      }
    );
  };

  return (
    <article
      id={`radar-opportunity-${opportunity.opportunity_id}`}
      className={`radar-card radar-opportunity-card${archived ? " is-archived" : ""}${focused ? " is-focused" : ""}`}
    >
      <div className="radar-card-topline">
        <span>{label(opportunity.practice_key)} opportunity</span>
        <span>{label(opportunity.state || "active")}</span>
      </div>
      <div className="radar-card-heading">
        <div>
          <h2>{opportunity.company_name}</h2>
          <p className="radar-muted">
            {opportunity.band.toUpperCase()} · {opportunity.evidence_count} governed evidence items
          </p>
        </div>
        {opportunity.snoozed_until ? (
          <span className="radar-follow-chip">
            Follow up {shortDate(opportunity.snoozed_until)}
          </span>
        ) : null}
      </div>
      <section className="radar-pursuit-proof" aria-label="Why this is a RADAR signal">
        <p className="radar-eyebrow">WHY THIS IS A RADAR SIGNAL</p>
        <p>{opportunity.summary}</p>
      </section>
      {opportunity.first_move ? (
        <section className="radar-pursuit-proof" aria-label="Suggested first move">
          <p className="radar-eyebrow">SUGGESTED FIRST MOVE</p>
          <p>{opportunity.first_move}</p>
        </section>
      ) : null}
      {!archived && opportunity.state !== "claimed" ? (
        <div className="radar-room-control">
          {mode === "idle" ? (
            <div className="radar-room-buttons">
              <button type="button" className="primary" onClick={() => setMode("claim")}>
                Claim or join
              </button>
              <button type="button" onClick={() => setMode("pass")}>
                Pass
              </button>
              <button type="button" onClick={() => setMode("snooze")}>
                Snooze
              </button>
              <button type="button" onClick={() => setMode("not_relevant")}>
                Not relevant
              </button>
              <button type="button" onClick={() => setMode("field_intel")}>
                Add field knowledge
              </button>
            </div>
          ) : null}
          {mode === "claim" ? (
            <div className="radar-inline-panel">
              <label>
                Your role
                <select value={role} onChange={(event) => setRole(event.target.value)}>
                  <option value="lead">Lead</option>
                  <option value="support">Support</option>
                  <option value="intro">Introduction</option>
                </select>
              </label>
              <label>
                Optional context
                <textarea value={reason} onChange={(event) => setReason(event.target.value)} />
              </label>
              <button
                type="button"
                className="primary"
                disabled={action.isPending}
                onClick={() => submit("claim", { role, reasonText: reason })}
              >
                Confirm claim
              </button>
              <button type="button" onClick={() => setMode("idle")}>
                Cancel
              </button>
            </div>
          ) : null}
          {mode === "pass" || mode === "not_relevant" ? (
            <div className="radar-inline-panel">
              <label>
                Reason
                <textarea value={reason} onChange={(event) => setReason(event.target.value)} />
              </label>
              <button
                type="button"
                className="primary"
                disabled={action.isPending}
                onClick={() => submit(mode, { reasonText: reason })}
              >
                Confirm {mode === "pass" ? "pass" : "not relevant"}
              </button>
              <button type="button" onClick={() => setMode("idle")}>
                Cancel
              </button>
            </div>
          ) : null}
          {mode === "snooze" ? (
            <div className="radar-inline-panel">
              <label>
                Follow up on
                <input
                  type="date"
                  value={followUpDate}
                  onChange={(event) => setFollowUpDate(event.target.value)}
                />
              </label>
              <button
                type="button"
                className="primary"
                disabled={action.isPending || !followUpDate}
                onClick={() => submit("snooze", { until: `${followUpDate}T12:00:00Z` })}
              >
                Set follow-up
              </button>
              <button type="button" onClick={() => setMode("idle")}>
                Cancel
              </button>
            </div>
          ) : null}
          {mode === "field_intel" ? (
            <div className="radar-inline-panel">
              <label>
                What do you know?
                <textarea
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  placeholder="Add first-hand account context, timing, stakeholders, or a specific next move."
                />
              </label>
              <button
                type="button"
                className="primary"
                disabled={action.isPending || !reason.trim()}
                onClick={() =>
                  submit("field_intel", {
                    reasonCode: "consultant_observation",
                    reasonText: reason,
                  })
                }
              >
                Add governed field knowledge
              </button>
              <button type="button" onClick={() => setMode("idle")}>
                Cancel
              </button>
            </div>
          ) : null}
          {savedMessage ? (
            <p className="radar-success" role="status">
              {savedMessage}
            </p>
          ) : null}
          {action.isError ? (
            <p className="radar-error" role="alert">
              {action.error instanceof Error
                ? action.error.message
                : "That RADAR response could not be saved."}
            </p>
          ) : null}
        </div>
      ) : null}
      <div className="radar-card-footer">
        <span>Private recipient alert · expires {shortDate(opportunity.expires_at)}</span>
        {opportunity.pursuit_id ? <span>Shared pursuit created</span> : null}
      </div>
    </article>
  );
}

function TrackingCard({ tracker }: { tracker: RadarCompanyTracker }): JSX.Element {
  const archive = useArchiveRadarTracker();
  const archived = tracker.status === "archived";
  return (
    <article className="radar-card radar-tracker-card">
      <div className="radar-card-topline">
        <span>Personal tracker</span>
        <span>
          {tracker.signal_count} {tracker.signal_count === 1 ? "signal" : "signals"}
        </span>
      </div>
      <div className="radar-card-heading">
        <div>
          <h2>{tracker.company_name}</h2>
          <p className="radar-muted">
            {tracker.identity_state === "resolved"
              ? "Governed company identity"
              : tracker.identity_state === "review_required"
                ? "Candidate identity is awaiting controlled review"
                : "Company research and identity resolution are queued"}
          </p>
        </div>
        <span
          className={`radar-status-orb${archived ? " is-archived" : ""}`}
          aria-label={archived ? "Tracking archived" : "Tracking active"}
        />
      </div>
      {tracker.recent_signals.length ? (
        <div className="radar-tracked-signals">
          <p className="radar-eyebrow">LATEST MATCHES</p>
          {tracker.recent_signals.map((signal) => (
            <div className="radar-tracked-signal" key={signal.signal_id}>
              <div>
                <strong>{signal.feed_title}</strong>
                <span>
                  {label(signal.signal_source)} · {shortDate(signal.last_observed_at)}
                  {signal.scf_score !== null ? ` · ${signal.scf_score}/100` : ""}
                </span>
              </div>
              {signal.signal_url ? (
                <a href={signal.signal_url} target="_blank" rel="noreferrer">
                  Source
                </a>
              ) : null}
            </div>
          ))}
        </div>
      ) : (
        <div className="radar-tracker-waiting">
          <strong>{archived ? "Tracker archived" : "Watching for the next governed signal"}</strong>
          <span>
            {archived
              ? "Prior company context remains available here for future research."
              : "New matching BDSE signals will collect here automatically."}
          </span>
        </div>
      )}
      <div className="radar-card-footer">
        <span>Tracking since {shortDate(tracker.created_at)}</span>
        {archived ? (
          <span className="radar-archive-state">Archived tracker</span>
        ) : (
          <button
            type="button"
            disabled={archive.isPending}
            onClick={() => archive.mutate(tracker.tracker_id)}
          >
            Archive tracker
          </button>
        )}
      </div>
    </article>
  );
}

function PursuitCard({
  pursuit,
  focused,
}: {
  pursuit: RadarPursuit;
  focused: boolean;
}): JSX.Element {
  const { profile } = useReadyAuth();
  const update = useUpdateRadarPursuit();
  const room = useRadarPursuitRoom();
  const [comment, setComment] = useState("");
  const [leadSelection, setLeadSelection] = useState("");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [successorEmail, setSuccessorEmail] = useState("");
  const [actionNotice, setActionNotice] = useState<string | null>(null);
  const [followUpDate, setFollowUpDate] = useState(pursuit.follow_up_at?.slice(0, 10) ?? "");
  const activeMembers = (pursuit.radar_pursuit_members ?? []).filter((member) => !member.left_at);
  // The canonical profile email is also what the server uses for pursuit
  // membership. It can intentionally differ from the Supabase login alias.
  const viewerEmail = profile.email.trim().toLowerCase();
  const viewerMember = activeMembers.find(
    (member) => member.member_email.trim().toLowerCase() === viewerEmail
  );
  const viewerIsLead = viewerMember?.member_role === "lead";
  const successorCandidates = activeMembers.filter(
    (member) => member.member_email.trim().toLowerCase() !== viewerEmail
  );
  const mentionsMuted = viewerMember?.is_muted === true;
  const archived = pursuit.status !== "active";

  const leavePursuit = () => {
    setActionNotice(null);
    room.mutate(
      { pursuitId: pursuit.pursuit_id, roomAction: "leave" },
      { onSuccess: () => setActionNotice("You left this pursuit.") }
    );
  };

  const transferAndLeave = () => {
    if (!successorEmail) return;
    setActionNotice(null);
    room.mutate(
      {
        pursuitId: pursuit.pursuit_id,
        roomAction: "resolve_lead",
        fields: { leadEmail: successorEmail },
      },
      {
        onSuccess: () =>
          room.mutate(
            { pursuitId: pursuit.pursuit_id, roomAction: "leave" },
            { onSuccess: () => setActionNotice("Lead transferred and pursuit left.") }
          ),
      }
    );
  };

  return (
    <article
      id={`radar-pursuit-${pursuit.pursuit_id}`}
      className={`radar-card radar-pursuit-card${archived ? " is-archived" : ""}${focused ? " is-focused" : ""}`}
    >
      <div className="radar-card-topline">
        <span>{label(pursuit.stage)}</span>
        <span>{label(pursuit.status)}</span>
      </div>
      <div className="radar-card-heading">
        <div>
          <h2>{pursuit.organizations?.legal_name ?? "Organization"}</h2>
          <p className="radar-muted">
            Lead: {pursuit.lead_name || pursuit.lead_email || "To be resolved"}
          </p>
        </div>
        {pursuit.follow_up_at ? (
          <span className="radar-follow-chip">Follow up {shortDate(pursuit.follow_up_at)}</span>
        ) : null}
      </div>
      {pursuit.lead_resolution_state === "needed" ? (
        <p className="radar-conflict">
          More than one person requested the lead. Resolve ownership before outreach.
        </p>
      ) : null}
      {pursuit.proofs?.length ? (
        <section className="radar-pursuit-proof" aria-label="Why this is a RADAR signal">
          <p className="radar-eyebrow">WHY THIS IS A RADAR SIGNAL</p>
          <div className="radar-proof-list">
            {pursuit.proofs.map((proof) => (
              <div className="radar-proof-item" key={proof.key}>
                <div className="radar-proof-equation">
                  {proof.url ? (
                    <a href={proof.url} target="_blank" rel="noreferrer">
                      {proof.label}
                      <span aria-hidden="true">↗</span>
                    </a>
                  ) : (
                    <strong>{proof.label}</strong>
                  )}
                  {(proof.suffixes ?? []).map((suffix) => (
                    <strong key={suffix}> + {suffix}</strong>
                  ))}
                </div>
                {proof.context ? <small>{proof.context}</small> : null}
              </div>
            ))}
          </div>
        </section>
      ) : null}
      <div className="radar-members">
        {activeMembers.map((member) => (
          <span key={member.member_email}>
            {member.member_name} · {label(member.member_role)}
          </span>
        ))}
      </div>

      {!archived ? (
        <div className="radar-card-controls">
          <label className="radar-stage-control">
            <span>Activity stage</span>
            <select
              value={pursuit.stage}
              disabled={update.isPending}
              onChange={(event) =>
                update.mutate({
                  pursuitId: pursuit.pursuit_id,
                  stage: event.target.value as RadarPursuit["stage"],
                })
              }
            >
              {STAGES.map((stage) => (
                <option value={stage} key={stage}>
                  {label(stage)}
                </option>
              ))}
            </select>
          </label>
          <label className="radar-stage-control">
            <span>Next follow-up</span>
            <div className="radar-follow-control">
              <input
                type="date"
                value={followUpDate}
                onChange={(event) => setFollowUpDate(event.target.value)}
              />
              <button
                type="button"
                disabled={room.isPending}
                onClick={() =>
                  room.mutate({
                    pursuitId: pursuit.pursuit_id,
                    roomAction: "follow_up",
                    fields: { dueAt: followUpDate ? `${followUpDate}T12:00:00Z` : "" },
                  })
                }
              >
                Save
              </button>
            </div>
          </label>
        </div>
      ) : null}

      {pursuit.lead_resolution_state === "needed" ? (
        <div className="radar-room-control radar-inline-panel">
          <label>
            Resolve lead
            <select
              value={leadSelection}
              onChange={(event) => setLeadSelection(event.target.value)}
            >
              <option value="">Choose a member…</option>
              {activeMembers.map((member) => (
                <option value={member.member_email} key={member.member_email}>
                  {member.member_name}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            disabled={room.isPending || !leadSelection}
            onClick={() =>
              room.mutate({
                pursuitId: pursuit.pursuit_id,
                roomAction: "resolve_lead",
                fields: { leadEmail: leadSelection },
              })
            }
          >
            Set pursuit lead
          </button>
        </div>
      ) : null}

      {!archived ? (
        <div className="radar-room-control">
          <label>
            Shared pursuit note
            <textarea
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              placeholder="Add context or mention a member with @their full name or @email…"
            />
          </label>
          {inviteOpen ? (
            <div className="radar-invite-panel">
              <label>
                <span>Consultant’s Ankura email</span>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(event) => setInviteEmail(event.target.value)}
                  placeholder="firstname.lastname@ankura.com"
                />
              </label>
              <button
                type="button"
                disabled={room.isPending || !inviteEmail.trim()}
                onClick={() =>
                  room.mutate(
                    {
                      pursuitId: pursuit.pursuit_id,
                      roomAction: "invite",
                      fields: { inviteEmail },
                    },
                    {
                      onSuccess: () => {
                        setInviteEmail("");
                        setInviteOpen(false);
                      },
                    }
                  )
                }
              >
                Add to pursuit
              </button>
            </div>
          ) : null}
          <div className="radar-room-buttons">
            <button
              type="button"
              className="primary"
              disabled={room.isPending || !comment.trim()}
              onClick={() =>
                room.mutate(
                  { pursuitId: pursuit.pursuit_id, roomAction: "comment", fields: { comment } },
                  { onSuccess: () => setComment("") }
                )
              }
            >
              Add note
            </button>
            <button type="button" onClick={() => setInviteOpen((current) => !current)}>
              Invite people
            </button>
            <button
              type="button"
              disabled={room.isPending}
              onClick={() =>
                room.mutate(
                  {
                    pursuitId: pursuit.pursuit_id,
                    roomAction: "mute",
                    fields: { muted: !mentionsMuted },
                  },
                  {
                    onSuccess: () =>
                      setActionNotice(
                        mentionsMuted
                          ? "Mentions are unmuted for this pursuit."
                          : "Mentions are muted for this pursuit."
                      ),
                  }
                )
              }
            >
              {mentionsMuted ? "Unmute mentions" : "Mute mentions"}
            </button>
            <button
              type="button"
              onClick={() => window.open(outlookDraftUrl(pursuit), "_blank", "noopener,noreferrer")}
            >
              Email this lead
            </button>
            <button
              type="button"
              disabled={room.isPending}
              onClick={() => room.mutate({ pursuitId: pursuit.pursuit_id, roomAction: "archive" })}
            >
              Archive
            </button>
            <button
              type="button"
              disabled={room.isPending}
              onClick={() => {
                if (viewerIsLead) {
                  setActionNotice(null);
                  setLeaveOpen(true);
                  return;
                }
                leavePursuit();
              }}
            >
              Leave pursuit
            </button>
          </div>
          {leaveOpen ? (
            <div className="radar-room-control radar-inline-panel" role="group">
              {successorCandidates.length ? (
                <>
                  <label>
                    Choose the new pursuit lead before leaving
                    <select
                      value={successorEmail}
                      onChange={(event) => setSuccessorEmail(event.target.value)}
                    >
                      <option value="">Choose a member…</option>
                      {successorCandidates.map((member) => (
                        <option value={member.member_email} key={member.member_email}>
                          {member.member_name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <button
                    type="button"
                    disabled={room.isPending || !successorEmail}
                    onClick={transferAndLeave}
                  >
                    Transfer lead and leave
                  </button>
                </>
              ) : (
                <>
                  <p>
                    You are the only member and current lead. Invite another consultant before
                    leaving so this pursuit always has an owner.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setInviteOpen(true);
                      setLeaveOpen(false);
                    }}
                  >
                    Invite a successor
                  </button>
                </>
              )}
              <button type="button" onClick={() => setLeaveOpen(false)}>
                Cancel
              </button>
            </div>
          ) : null}
          <small className="radar-email-note">
            Email this lead opens a populated Outlook draft. Nothing is sent automatically.
          </small>
          {actionNotice ? (
            <p className="radar-success" role="status">
              {actionNotice}
            </p>
          ) : null}
        </div>
      ) : null}
      {room.isError ? (
        <p className="radar-error" role="alert">
          {room.error instanceof Error
            ? room.error.message
            : "That pursuit action could not be completed."}
        </p>
      ) : null}
      <details className="radar-activity">
        <summary>Recent activity</summary>
        {(pursuit.radar_pursuit_events ?? []).slice(0, 8).map((event) => (
          <p key={event.event_id}>
            <strong>{label(event.event_type)}</strong> · {shortDate(event.created_at)}
            {event.body ? <span> — {event.body}</span> : null}
          </p>
        ))}
      </details>
    </article>
  );
}

export function RadarWorkspace(): JSX.Element {
  const status = useRadarStatus();
  const collaboration = useRadarCollaborationStatus();
  const pursuitProofs = useRadarPursuitProofs((status.data?.pursuits.length ?? 0) > 0);
  const [params, setParams] = useSearchParams();
  const filter = viewFor(params.get("view"));
  const focusedPursuitId = params.get("pursuit");
  const focusedOpportunityId = params.get("opportunity");
  const [archiveScope, setArchiveScope] = useState<"all" | "my">("all");
  const [archiveSearch, setArchiveSearch] = useState("");
  const archives = useRadarArchives(archiveScope, archiveSearch, filter === "archive");
  const pursuits = useMemo(() => {
    const stateByPursuit = new Map(
      (collaboration.data?.pursuitState ?? []).map((row) => [row.pursuit_id, row])
    );
    const proofsByPursuit = new Map(
      (pursuitProofs.data ?? []).map((row) => [row.pursuit_id, row.proofs])
    );
    return (status.data?.pursuits ?? []).map((pursuit) => ({
      ...pursuit,
      ...stateByPursuit.get(pursuit.pursuit_id),
      proofs: proofsByPursuit.get(pursuit.pursuit_id) ?? pursuit.proofs,
    }));
  }, [collaboration.data?.pursuitState, pursuitProofs.data, status.data?.pursuits]);
  const opportunities = useMemo(
    () => status.data?.opportunities ?? [],
    [status.data?.opportunities]
  );
  const trackers = useMemo(
    () => collaboration.data?.trackers ?? [],
    [collaboration.data?.trackers]
  );

  const visibleOpportunities = useMemo(
    () => opportunities.filter((opportunity) => opportunityMatchesView(opportunity, filter)),
    [filter, opportunities]
  );

  const visiblePursuits = useMemo(
    () =>
      (filter === "archive" ? (archives.data?.pursuits ?? []) : pursuits).filter((pursuit) => {
        return pursuitMatchesView(pursuit, filter);
      }),
    [archives.data?.pursuits, filter, pursuits]
  );
  const visibleTrackers = trackers.filter((tracker) => {
    if (filter !== "archive") {
      return trackerMatchesView(tracker, filter);
    }
    if (archiveScope !== "my" || tracker.status !== "archived") return false;
    const query = archiveSearch.trim().toLowerCase();
    if (!query) return true;
    const searchable = [
      tracker.company_name,
      tracker.identity_note,
      tracker.identity_state,
      ...tracker.recent_signals.flatMap((signal) => [signal.feed_title, signal.signal_source]),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return searchable.includes(query);
  });
  const counts: Record<RadarView, number> = {
    all:
      pursuits.filter((pursuit) => pursuit.status === "active").length +
      trackers.filter((tracker) => tracker.status === "active").length +
      opportunities.filter(
        (opportunity) =>
          !opportunity.pursuit_id &&
          opportunity.state !== "claimed" &&
          !isArchivedOpportunity(opportunity)
      ).length,
    opportunities: opportunities.filter(
      (opportunity) =>
        !opportunity.pursuit_id &&
        opportunity.state !== "claimed" &&
        !isArchivedOpportunity(opportunity)
    ).length,
    tracking: trackers.filter((tracker) => tracker.status === "active").length,
    claimed: pursuits.filter(
      (pursuit) => pursuit.status === "active" && pursuit.stage === "claimed"
    ).length,
    outreach: pursuits.filter(
      (pursuit) =>
        pursuit.status === "active" && ["outreach", "meeting", "proposal"].includes(pursuit.stage)
    ).length,
    follow_ups:
      pursuits.filter((pursuit) => pursuit.status === "active" && pursuit.follow_up_at).length +
      opportunities.filter(
        (opportunity) => !opportunity.pursuit_id && opportunity.state === "snoozed"
      ).length,
    archive:
      (archives.data?.pursuits.length ??
        pursuits.filter((pursuit) => pursuit.status !== "active").length) +
      trackers.filter((tracker) => tracker.status === "archived").length +
      opportunities.filter(isArchivedOpportunity).length,
  };
  const isEmpty =
    status.isSuccess &&
    collaboration.isSuccess &&
    !(filter === "archive" && archives.isPending) &&
    visiblePursuits.length === 0 &&
    visibleTrackers.length === 0 &&
    visibleOpportunities.length === 0;

  useEffect(() => {
    if ((!focusedPursuitId && !focusedOpportunityId) || !status.isSuccess) return;
    window.requestAnimationFrame(() => {
      document
        .getElementById(
          focusedPursuitId
            ? `radar-pursuit-${focusedPursuitId}`
            : `radar-opportunity-${focusedOpportunityId}`
        )
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }, [focusedOpportunityId, focusedPursuitId, status.isSuccess]);

  return (
    <div className="radar-page">
      <header className="radar-hero">
        <div className="radar-hero-copy">
          <h1 className="radar-wordmark-title" aria-label="My RADAR">
            <span aria-hidden="true" className="radar-title-my">
              My
            </span>
            <span aria-hidden="true" className="radar-title-brand">
              R<span className="radar-title-a">Λ</span>D<span className="radar-title-a">Λ</span>R
            </span>
          </h1>
          <p>
            Track companies, coordinate early pursuits, and move qualified context toward
            outreach—before it reaches Salesforce.
          </p>
        </div>
      </header>

      {status.isPending ? <div className="radar-empty">Loading your RADAR workspace…</div> : null}
      {status.isError ? (
        <div className="radar-empty radar-error">RADAR could not load. Refresh to try again.</div>
      ) : null}
      {status.data && !status.data.enabled ? (
        <div className="radar-notice">
          <strong>RADAR is in controlled rollout.</strong> The workspace is installed but alert
          generation remains dark-launched.
        </div>
      ) : null}

      <nav className="radar-toolbar" aria-label="RADAR views">
        {RADAR_VIEWS.map((view) => (
          <button
            type="button"
            key={view}
            onClick={() => setParams(view === "all" ? {} : { view })}
            className={filter === view ? "active" : ""}
          >
            <span>{label(view)}</span>
            <strong>{counts[view]}</strong>
          </button>
        ))}
      </nav>

      {filter === "archive" ? (
        <section className="radar-archive-controls" aria-label="Archive filters">
          <label>
            Archive view
            <select
              value={archiveScope}
              onChange={(event) => setArchiveScope(event.target.value === "my" ? "my" : "all")}
            >
              <option value="all">All Archives</option>
              <option value="my">My Archives</option>
            </select>
          </label>
          <label className="radar-archive-search">
            Search archives
            <input
              type="search"
              value={archiveSearch}
              onChange={(event) => setArchiveSearch(event.target.value)}
              placeholder="Company, practice, person, note, or evidence"
            />
          </label>
          <p>
            {archives.data?.canReadHistorical
              ? "Historical research access: all retained RADAR records are available."
              : "All BDSE users can review shared RADAR context from the last 180 days."}
          </p>
        </section>
      ) : null}

      {filter === "archive" && archives.isPending ? (
        <div className="radar-empty">Searching the RADAR archive…</div>
      ) : null}

      {isEmpty ? (
        <div className="radar-empty">
          <span className="radar-empty-orb" aria-hidden="true" />
          <h2>No leads in {label(filter)}</h2>
          <p>
            {filter === "tracking"
              ? "Use Company Search to make any company a personal RADAR signal."
              : "Qualified RADAR activity will appear here as it develops."}
          </p>
        </div>
      ) : (
        <section className="radar-grid" aria-label={`${label(filter)} RADAR leads`}>
          {visibleOpportunities.map((opportunity) => (
            <AlertOpportunityCard
              opportunity={opportunity}
              focused={opportunity.opportunity_id === focusedOpportunityId}
              key={opportunity.alert_recipient_id}
            />
          ))}
          {visibleTrackers.map((tracker) => (
            <TrackingCard tracker={tracker} key={tracker.tracker_id} />
          ))}
          {visiblePursuits.map((pursuit) => (
            <PursuitCard
              pursuit={pursuit}
              focused={pursuit.pursuit_id === focusedPursuitId}
              key={pursuit.pursuit_id}
            />
          ))}
        </section>
      )}
    </div>
  );
}
